import { useEffect, useRef, useState } from "react"
import { HexColorPicker } from "react-colorful"
import { FormField } from "@/components/shared/form-field"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

const HEX_RE = /^#[0-9a-f]{6}$/i

export function ColorField({ id, label, description, value, onChange, disabled = false, fallback }: {
  id: string
  label: string
  description?: string
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  fallback?: string
}) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const emptyAllowed = value === "" && fallback !== undefined && HEX_RE.test(fallback)
  const valid = HEX_RE.test(value) || emptyAllowed
  const normalized = HEX_RE.test(value) ? value : emptyAllowed ? fallback : "#000000"

  useEffect(() => {
    if (!open) return
    function handlePointer(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", handlePointer)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("mousedown", handlePointer)
      document.removeEventListener("keydown", handleKey)
    }
  }, [open])

  return (
    <FormField htmlFor={id} label={label} description={description} error={!valid ? "Use uma cor hexadecimal completa, como #6366F1." : undefined}>
      <div className="relative flex min-w-0 items-center gap-2" ref={wrapperRef}>
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          disabled={disabled}
          aria-label={`Abrir seletor de cor para ${label.toLowerCase()}`}
          aria-expanded={open}
          className={cn(
            "relative h-9 w-9 shrink-0 rounded-lg overflow-hidden border border-border/80 bg-card",
            "cursor-pointer transition-colors hover:border-foreground/30 disabled:cursor-not-allowed disabled:opacity-50",
            open && "border-foreground/40",
          )}
        >
          <span
            className="absolute inset-0"
            style={{ backgroundColor: normalized }}
          />
          <span
            aria-hidden
            className="absolute inset-0 opacity-30 bg-[linear-gradient(45deg,#9ca3af_25%,transparent_25%,transparent_75%,#9ca3af_75%),linear-gradient(45deg,#9ca3af_25%,transparent_25%,transparent_75%,#9ca3af_75%)] bg-[length:8px_8px] bg-[position:0_0,4px_4px]"
          />
        </button>
        <Input
          id={id}
          value={value}
          aria-invalid={!valid}
          onChange={(event) => { if (/^#[0-9a-f]{0,6}$/i.test(event.target.value)) onChange(event.target.value.toUpperCase()) }}
          className="min-w-0 flex-1 font-mono uppercase h-9 rounded-lg border-border/80 bg-card font-bold tracking-normal text-xs shadow-2xs"
          maxLength={7}
          disabled={disabled}
        />
        {open && (
          <div className="absolute left-0 top-11 z-50 rounded-xl border border-border/80 bg-card p-3 shadow-xl">
            <HexColorPicker color={normalized} onChange={(next) => onChange(next.toUpperCase())} />
            <div className="mt-2.5 flex items-center justify-between gap-2 text-[11px]">
              <span className="font-mono font-bold text-foreground">{normalized.toUpperCase()}</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-xs font-semibold text-muted-foreground hover:text-foreground"
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </div>
    </FormField>
  )
}
